/**
 * FileMakerModule.ts - Configgr module for FileMaker Data API settings
 * 
 * CHANGE HISTORY:
 * - 251031_1153 danielbeer: Initial creation for FileMaker configuration
 */

/**
 * FileMaker Configgr Module
 * Provides global settings for FileMaker Data API connections
 */
export const filemakerModule = {
  id: 'filemaker-datasource',
  name: 'FileMaker Data API',
  icon: '🗄️',
  category: 'services',
  order: 10,
  description: 'FileMaker Data API connection settings and defaults',
  
  settings: {
    defaultServer: {
      type: 'string',
      label: 'Default FileMaker Server',
      description: 'Default server URL for FileMaker Data API connections',
      placeholder: 'https://fms.example.com',
      defaultValue: '',
      scope: ['tenant', 'user']
    },
    
    defaultDatabase: {
      type: 'string',
      label: 'Default Database',
      description: 'Default database name for new connections',
      placeholder: 'CRM',
      defaultValue: '',
      scope: ['tenant', 'user']
    },
    
    defaultLayout: {
      type: 'string',
      label: 'Default Layout',
      description: 'Default layout name for new connections',
      placeholder: 'API_Layout',
      defaultValue: '',
      scope: ['tenant', 'user']
    },
    
    useKeystore: {
      type: 'boolean',
      label: 'Store credentials in keystore',
      description: 'Use secure keystore (Vault/AWS/Azure) for FileMaker credentials',
      defaultValue: true,
      scope: ['tenant']
    },
    
    keystoreProvider: {
      type: 'select',
      label: 'Keystore Provider',
      description: 'Which keystore provider to use for credential storage',
      defaultValue: 'vault',
      options: [
        { value: 'vault', label: 'HashiCorp Vault' },
        { value: 'aws-secrets', label: 'AWS Secrets Manager' },
        { value: 'azure-keyvault', label: 'Azure Key Vault' },
        { value: 'gcp-secrets', label: 'Google Secret Manager' },
        { value: 'local-encrypted', label: 'Local Encrypted Storage' },
        { value: 'env', label: 'Environment Variables' }
      ],
      scope: ['tenant']
    },
    
    connectionTimeout: {
      type: 'number',
      label: 'Connection timeout (seconds)',
      description: 'Maximum time to wait for FileMaker server connection',
      defaultValue: 30,
      scope: ['tenant', 'user'],
      validation: (value: number) => value > 0 && value <= 300
    },
    
    requestTimeout: {
      type: 'number',
      label: 'Request timeout (seconds)',
      description: 'Maximum time to wait for FileMaker API requests',
      defaultValue: 60,
      scope: ['tenant', 'user'],
      validation: (value: number) => value > 0 && value <= 300
    },
    
    maxRetries: {
      type: 'number',
      label: 'Maximum retries',
      description: 'Number of times to retry failed requests',
      defaultValue: 3,
      scope: ['tenant', 'user'],
      validation: (value: number) => value >= 0 && value <= 10
    },
    
    enableCaching: {
      type: 'boolean',
      label: 'Enable response caching',
      description: 'Cache FileMaker API responses for better performance',
      defaultValue: true,
      scope: ['user']
    },
    
    cacheDuration: {
      type: 'number',
      label: 'Cache duration (minutes)',
      description: 'How long to cache FileMaker API responses',
      defaultValue: 5,
      scope: ['user'],
      validation: (value: number) => value > 0 && value <= 60
    },
    
    enableDebugLogging: {
      type: 'boolean',
      label: 'Enable debug logging',
      description: 'Log detailed FileMaker API operations to console',
      defaultValue: false,
      scope: ['user']
    },
    
    verifySSL: {
      type: 'boolean',
      label: 'Verify SSL certificates',
      description: 'Verify SSL certificates when connecting to FileMaker Server',
      defaultValue: true,
      scope: ['tenant']
    },
    
    apiVersion: {
      type: 'select',
      label: 'FileMaker Data API Version',
      description: 'Which version of the FileMaker Data API to use',
      defaultValue: 'v1',
      options: [
        { value: 'v1', label: 'Version 1 (FileMaker 17+)' },
        { value: 'v2', label: 'Version 2 (FileMaker 19+)' }
      ],
      scope: ['tenant']
    },
    
    defaultRecordLimit: {
      type: 'number',
      label: 'Default record limit',
      description: 'Default maximum number of records to fetch per request',
      defaultValue: 100,
      scope: ['user'],
      validation: (value: number) => value > 0 && value <= 1000
    },
    
    enableFieldMapping: {
      type: 'boolean',
      label: 'Enable automatic field mapping',
      description: 'Automatically map FileMaker fields to standard field names',
      defaultValue: true,
      scope: ['user']
    },
    
    testConnectionOnStartup: {
      type: 'boolean',
      label: 'Test connection on startup',
      description: 'Verify FileMaker server connection when application starts',
      defaultValue: false,
      scope: ['user']
    }
  }
};

export default filemakerModule;
